import React, { useState } from "react";
import axios from "axios";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  { id: "instagram", icon: Instagram, label: "Instagram" },
  { id: "facebook", icon: Facebook, label: "Facebook" },
  { id: "linkedin", icon: Linkedin, label: "LinkedIn" },
];

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Welcome.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="newsletter-form">
      <Label>Quiet updates, not noise</Label>
      <div className="mt-3 flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="your@email.com"
          data-testid="newsletter-email"
          className="flex-1 bg-white border border-gray-200 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[var(--gg-primary)]"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="newsletter-submit"
          className="bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 transition-colors rounded-full px-6 py-3 text-sm"
        >
          {busy ? "..." : "Subscribe"}
        </button>
      </div>
    </form>
  );
};

const SocialLinks = () => (
  <div className="flex items-center gap-3">
    {SOCIALS.map((s) => {
      const Icon = s.icon;
      return (
        <a
          key={s.id}
          href="#"
          data-testid={`social-${s.id}`}
          aria-label={s.label}
          className="w-9 h-9 rounded-full border border-[var(--gg-secondary)]/60 flex items-center justify-center text-[var(--gg-primary)] hover:bg-[var(--gg-primary)] hover:text-white transition-colors"
        >
          <Icon size={15} />
        </a>
      );
    })}
  </div>
);

const FooterNav = () => (
  <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
    <FooterCol title="Services">
      <FooterLink href="#care">In-Home Care</FooterLink>
      <FooterLink href="#packages">Relocation Packages</FooterLink>
      <FooterLink href="#addons">À La Carte Add-Ons</FooterLink>
      <FooterLink href="#property">Property Management</FooterLink>
    </FooterCol>
    <FooterCol title="Company">
      <FooterLink href="/#about">About Us</FooterLink>
      <FooterLink href="/#for-whom">Who We Serve</FooterLink>
      <FooterLink href="/#blog">Graceful Blog</FooterLink>
      <FooterLink href="/#faq">FAQ</FooterLink>
    </FooterCol>
    <FooterCol title="Contact">
      <FooterLink href="tel:+18764496400">Jamaica · 876-449-6400</FooterLink>
      <FooterLink href="tel:+14373472020">Canada · 437-347-2020</FooterLink>
      <FooterLink href="tel:+12035506378">USA · 203-550-6378</FooterLink>
      <FooterLink href="mailto:info@caregracefulguidance.com">
        info@caregracefulguidance.com
      </FooterLink>
      <FooterLink href="#contact">Book Consultation</FooterLink>
    </FooterCol>
  </div>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[var(--gg-cream)] border-t border-[var(--gg-secondary)]/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <BrandMark variant="stack" className="mb-2" />
            <p className="text-sm text-[var(--gg-text-muted)] mt-4 max-w-sm leading-relaxed">
              Compassionate senior support and dignified relocation across
              Jamaica.
            </p>
            <p
              data-testid="footer-address"
              className="text-sm text-[var(--gg-deep)] mt-3 max-w-sm leading-relaxed font-medium"
            >
              Ironshore, Montego Bay, Jamaica
            </p>
            <NewsletterForm />
          </div>
          <FooterNav />
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--gg-secondary)]/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="text-xs text-[var(--gg-text-muted)] max-w-2xl leading-relaxed">
            © {new Date().getFullYear()} Graceful Guidance Ltd. All rights
            reserved. Services advisory and coordination-based; not legal,
            brokerage, or financial advice. HEART Trust NTA certified team.
          </div>
          <SocialLinks />
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.22em] text-[var(--gg-primary)] mb-4">
        {title}
      </h4>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}
function FooterLink({ href, children }) {
  return (
    <li>
      <a
        href={href}
        className="text-sm text-[var(--gg-text)] hover:text-[var(--gg-primary)] transition-colors"
      >
        {children}
      </a>
    </li>
  );
}
function Label({ children }) {
  return (
    <span className="text-xs uppercase tracking-[0.22em] text-[var(--gg-text-muted)]">
      {children}
    </span>
  );
}
